import * as Api from '@ViewModels';
import React from 'react';
import { useUserSession } from '../../models/hooks/appStateHooks';

export interface UseLoadAllUsersOptions {
	pageSize?: number;
	impersonationContext?: Api.IImpersonationContext;
	onError?: (error: string) => void;
}

export interface UseLoadAllUsersResult {
	users: Api.IUser[];
	isLoading: boolean;
	error: string | null;
	loadAllUsers: () => Promise<void>;
}

/**
 * Hook for loading all users using direct API calls with pagination.
 * This bypasses React Query's infinite query limitations by implementing
 * the same pagination logic used in the admin panel.
 */
export function useLoadAllUsers({
	pageSize = 500,
	impersonationContext,
	onError,
}: UseLoadAllUsersOptions = {}): UseLoadAllUsersResult {
	const userSession = useUserSession();
	const [users, setUsers] = React.useState<Api.IUser[]>([]);
	const [isLoading, setIsLoading] = React.useState(false);
	const [error, setError] = React.useState<string | null>(null);

	const loadAllUsers = React.useCallback(async () => {
		if (isLoading) return;

		setIsLoading(true);
		setError(null);

		try {
			const allUsers: Api.IUser[] = [];
			let pageToken: string | undefined;
			let previousCount = 0;
			let currentCount = 0;
			let retryCount = 0;
			const maxRetries = 3;

			// Admin logic: always load at least once, then keep loading until no new users are added
			do {
				previousCount = currentCount;

				try {
					const result = await userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.IUser>>(
						Api.ImpersonationBroker.composeApiUrl({
							impersonationContext,
							queryParams: { pageSize, pageToken },
							urlPath: `user/`,
						}),
						'GET',
						null
					);

					if (result.values && result.values.length > 0) {
						allUsers.push(...result.values);
						// Update state progressively during loading
						setUsers([...allUsers]);
					}

					pageToken = result.pageToken;
					retryCount = 0; // Reset retry count on success
				} catch {
					retryCount++;

					if (retryCount >= maxRetries) {
						throw new Error(`Failed to load users after ${maxRetries} attempts`);
					}

					// Exponential backoff: 200ms, 400ms, 800ms
					const retryDelay = 200 * Math.pow(2, retryCount - 1);
					await new Promise(resolve => setTimeout(resolve, retryDelay));
					continue;
				}

				currentCount = allUsers.length;

				// Safety check - if pageToken is null/undefined, we should stop
				if (!pageToken) {
					break;
				}
			} while (currentCount > previousCount);

			// Final state update
			setUsers(allUsers);
		} catch (loadError) {
			const errorMessage = loadError instanceof Error ? loadError.message : 'Failed to load all users';
			setError(errorMessage);
			onError?.(errorMessage);
		} finally {
			setIsLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userSession, impersonationContext, pageSize, onError]);

	return {
		users,
		isLoading,
		error,
		loadAllUsers,
	};
}
